import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Layout from "../components/layoutBlack";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { hideLoader } from "../store/slices/loader.ts";
import { setOrderData, setPriceData } from "../store/slices/cartSlice.ts";

const ChooseCarrier = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const items = useSelector((state) => state.cart.items);
  const subtotal = useSelector((state) => state.cart.subtotal || 0);
  const discountValue = useSelector((state) => state.cart.discount_value || 0);
  const selectedRatesId = useSelector((state) => state.cart.rates_id || "");

  const shippingInfo = useSelector((state) => state.cart.shipping_info);
  const shippingRates = useSelector((state) => state.cart.shipping_rates || {});

  const baseTotal = parseFloat(subtotal || 0) - parseFloat(discountValue || 0);

  useEffect(() => {
    dispatch(hideLoader());
  }, [dispatch]);

  useEffect(() => {
    const hasRates = shippingRates && Object.keys(shippingRates).length > 0;
    const hasShippingInfo =
      shippingInfo &&
      shippingInfo.firstName &&
      shippingInfo.lastName &&
      shippingInfo.street1 &&
      shippingInfo.city &&
      shippingInfo.country;

    if (!hasRates || !hasShippingInfo) {
      navigate("/shipping-info", { replace: true });
    }
  }, [shippingRates, shippingInfo, navigate]);

  const adjustedShippingRates = useMemo(() => {
    const cloned = Object.fromEntries(
      Object.entries(shippingRates || {}).map(([k, v]) => [k, { ...v }])
    );

    if (
      parseFloat(baseTotal) >= 25 &&
      Object.keys(cloned).length > 0 &&
      shippingInfo?.country === "US"
    ) {
      let lowestId = null;
      let lowestAmount = Infinity;

      for (const [id, rate] of Object.entries(cloned)) {
        const amount = parseFloat(rate.amount);
        if (!Number.isNaN(amount) && amount < lowestAmount) {
          lowestAmount = amount;
          lowestId = id;
        }
      }

      if (lowestId) {
        cloned[lowestId].amount = "0.00";
      }
    }

    return cloned;
  }, [shippingRates, baseTotal, shippingInfo]);

  const initialValues = {
    selectedRate: selectedRatesId,
  };

  const validationSchema = Yup.object({
    selectedRate: Yup.string().required("Please select a shipping option"),
  });

  return (
    <Layout>
      <section className="full-page-section bg0 p-t-120 p-b-80">
        <div className="container">
          <div className="row isotope-grid">
            <section className="full-page-section bg0 p-t-62 p-b-60">
              <div className="container">
                <div className="row">
                  <div className="col-lg-5 mb-5">
                    <h4 className="mb-3">Shipping Address</h4>

                    <div className="border p-3 rounded mb-3 bg-light">
                      <p className="mb-1">
                        <strong>
                          {shippingInfo?.firstName} {shippingInfo?.lastName}
                        </strong>
                      </p>
                      <p className="mb-1">{shippingInfo?.street1}</p>
                      {shippingInfo?.street2 && (
                        <p className="mb-1">{shippingInfo.street2}</p>
                      )}
                      <p className="mb-1">
                        {shippingInfo?.city}, {shippingInfo?.state}{" "}
                        {shippingInfo?.zip}
                      </p>
                      <p className="mb-1">{shippingInfo?.country}</p>
                      <p className="mb-1">{shippingInfo?.email}</p>
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline-secondary mb-4 w-100"
                      onClick={() => navigate("/shipping-info")}
                    >
                      Edit Address
                    </button>

                    <h4 className="mb-3">Choose Shipping Option</h4>

                    <Formik
                      enableReinitialize
                      initialValues={initialValues}
                      validationSchema={validationSchema}
                      onSubmit={(values, { setSubmitting }) => {
                        const selected =
                          adjustedShippingRates?.[values.selectedRate];

                        if (!selected) {
                          setSubmitting(false);
                          return;
                        }

                        const shippingCost = parseFloat(selected.amount || 0);
                        const grandTotal = parseFloat(baseTotal || 0) + shippingCost;

                        dispatch(
                          setOrderData({
                            rates_id: values.selectedRate,
                            shipping_price: shippingCost,
                            carrier: selected.provider || null,
                            service_level: selected.service_level || null,
                          })
                        );

                        dispatch(
                          setPriceData({
                            total_price: grandTotal,
                          })
                        );

                        console.log("Navigating to payment...");
                        navigate("/payment");
                        setSubmitting(false);
                      }}
                    >
                      {({ isSubmitting, values }) => {
                        const selected =
                          adjustedShippingRates?.[values.selectedRate];
                        const shippingAmount = selected
                          ? parseFloat(selected.amount || 0)
                          : 0;
                        const combinedTotal = (
                          parseFloat(baseTotal || 0) + shippingAmount
                        ).toFixed(2);

                        return (
                          <Form>
                            <div className="mb-3">
                              {Object.entries(adjustedShippingRates || {}).map(
                                ([id, rate]) => (
                                  <div className="form-check mb-2" key={id}>
                                    <Field
                                      type="radio"
                                      name="selectedRate"
                                      value={id}
                                      className="form-check-input"
                                      id={`rate-${id}`}
                                    />
                                    <label
                                      htmlFor={`rate-${id}`}
                                      className="form-check-label"
                                    >
                                      {rate.provider} - {rate.service_level} ($
                                      {rate.amount}) — {rate.estimated_days} day(s)
                                    </label>
                                  </div>
                                )
                              )}

                              <ErrorMessage
                                name="selectedRate"
                                component="div"
                                className="text-danger small mt-1"
                              />
                            </div>

                            <div className="mt-3">
                              <strong>Shipping:</strong>{" "}
                              {selected ? `$${shippingAmount.toFixed(2)}` : "—"}
                            </div>

                            <div className="mt-1 mb-3">
                              <strong>Total:</strong> ${combinedTotal}
                            </div>

                            <button
                              type="submit"
                              className="btn btn-primary w-100"
                              disabled={isSubmitting}
                            >
                              Continue to Payment
                            </button>
                          </Form>
                        );
                      }}
                    </Formik>
                  </div>

                  <div className="col-lg-7">
                    <h4 className="mb-4">Your Cart</h4>

                    <div className="table-responsive">
                      <table className="table">
                        <thead className="table-light">
                          <tr>
                            <th>Image</th>
                            <th>Product</th>
                            <th>Price</th>
                            <th>Qty</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  style={{ width: "50px" }}
                                />
                              </td>
                              <td>{item.name}</td>
                              <td>${item.price.toFixed(2)}</td>
                              <td>{item.quantity}</td>
                              <td>${(item.price * item.quantity).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                      <div>
                        <div>
                          <strong>Subtotal:</strong>
                        </div>
                        {parseFloat(discountValue) > 0 && (
                          <div>
                            <strong>Discount:</strong>
                          </div>
                        )}
                        <div>
                          <strong>Shipping:</strong>
                        </div>
                        <div>
                          <strong>Total:</strong>
                        </div>
                      </div>

                      <div className="text-end">
                        <div>${parseFloat(subtotal).toFixed(2)}</div>
                        {parseFloat(discountValue) > 0 && (
                          <div className="text-success">
                            – ${parseFloat(discountValue).toFixed(2)}
                          </div>
                        )}
                        <div>Calculated after selection</div>
                        <div>
                          <strong>Choose a rate</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ChooseCarrier;